import type { eOfflineValue, IOfflineListener } from '@microsoft/applicationinsights-common';
import type { IExtendedConfiguration, IPayloadData, IPlugin } from '@microsoft/1ds-analytics-js';
import type { DataContexts } from '@microsoft/1ds-privacy-guard-js';
import type { CoreAnalyticsOptions } from 'owa-analytics-types';
import type { AccountKey } from 'owa-client-types';
import type { DiagnosticOverrides } from './utils/addCommonDiagnosticProperties';
import type { AppInisightsTenants, CachedProperties } from './types/OneDsWrapperTypes';
import {
    type eStorageProviders,
    type IOfflineChannelConfiguration,
    OfflineChannel,
} from '@microsoft/applicationinsights-offlinechannel-js';
import { PrivacyGuardPlugin } from '@microsoft/1ds-privacy-guard-js';
import { ApplicationInsights, NRT_PROFILE } from '@microsoft/1ds-analytics-js';
import pako from 'pako';
import { trace } from 'owa-trace';
import { SchemaEnforcementPlugin } from './plugins/SchemaEnforcementPlugin';
import { EcsScorecardSizePlugin } from './plugins/EcsScorecardSizePlugin';
import getUserAgentString from 'owa-user-agent/lib/utils/getUserAgentString';
import { subscribeToNetworkChange } from 'owa-offline/lib/subscribeToNetworkChange';
import { getClientVersion } from 'owa-config/lib/bootstrapOptions';
import { getSessionId } from 'owa-config/lib/getSessionId';
import { getAnalyticsFlightsAndAppSettings } from './settings/getAnalyticsFlightsAndAppSettings';
import { getCachedSettings } from './settings/getCachedSettings';
import { getOfflineTelemetryDatabasePrefix } from './utils/getOfflineTelemetryDatabasePrefix';
import { addCommonProperties } from './utils/addCommonProperties';
import { captureSdkEcsConfigState } from './utils/sdkEcsConfigState';

/**
 * This will initialize the 1DS SDK for a new tenant when we receive an new event
 */
export async function initializeAppInsights(
    insights: AppInisightsTenants,
    cachedProperties: CachedProperties,
    endpointUrl: string | undefined,
    tenantToken: string,
    analyticsInstanceKey: string = tenantToken,
    analyticsOptions?: CoreAnalyticsOptions,
    skipCommonProperties?: boolean,
    useUnifiedSchema?: boolean,
    diagnosticOverrides: DiagnosticOverrides | null = null,
    accountKey: AccountKey | 'globalAccount' = 'globalAccount'
): Promise<void> {
    trace.info(
        ` ${
            insights[analyticsInstanceKey] ? 'Updating existing' : 'Initializing new'
        } 1DS tenant. Tenant: ${tenantToken}. AccountKey: ${accountKey}. Hash: ${analyticsInstanceKey}. EndpointUrl: ${endpointUrl}`,
        'analytics'
    );

    if (insights[analyticsInstanceKey]) {
        cachedProperties[analyticsInstanceKey].offlineListener?.unload();
        cachedProperties[analyticsInstanceKey].unsubscribe?.();

        // Use timeout to allow internal oneds timers to complete
        const oldInsights = insights[analyticsInstanceKey];
        setTimeout(() => oldInsights.unload(), 30_000);
    }

    insights[analyticsInstanceKey] = new ApplicationInsights();
    const config: IExtendedConfiguration = {
        instrumentationKey: tenantToken,
        endpointUrl,
        disableCookiesUsage: true, // This stops sending installId in the SDK section
        channelConfiguration: {
            payloadPreprocessor: gzipFunc,
        },
        enableWParam: true, // Set the "w" parameters so the 1ds collector can parse the web content when it comes from the web worker
        propertyConfiguration: {
            populateBrowserInfo: true,
            populateOperatingSystemInfo: true,
            userAgent: getUserAgentString(),
        },
    };

    // Schema enforcement prevents new Aria tables/columns from being created
    // by typos or injection. Gated behind the fwk-schema-enforcement flight.
    const plugins: IPlugin[] = [];
    if (getAnalyticsFlightsAndAppSettings(accountKey)?.enableSchemaEnforcement) {
        plugins.push(new SchemaEnforcementPlugin());
    }
    if (
        getAnalyticsFlightsAndAppSettings(accountKey)?.enablePrivacyGuard ||
        analyticsOptions?.enablePrivacyGuard
    ) {
        const privacyGuard: PrivacyGuardPlugin = new PrivacyGuardPlugin();
        const sessionSettings = getCachedSettings(accountKey)?.userConfiguration?.SessionSettings;

        const dataCtx: DataContexts = {
            UserName: sessionSettings?.UserDisplayName,
            UserAlias: sessionSettings?.UserPrincipalName?.split('@')[0],
            IgnoredNameParts: ['Microsoft', 'Dogfood'],
        };

        config.extensionConfig = {
            [privacyGuard.identifier]: {
                context: dataCtx,
                scanForUrls: false,
            },
        };
        plugins.push(privacyGuard);
    }

    plugins.push(new EcsScorecardSizePlugin());
    insights[analyticsInstanceKey].initialize(config, plugins);
    insights[analyticsInstanceKey].getPostChannel()?._setTransmitProfile(NRT_PROFILE);

    // Common properties should only be initialized once per tenant
    // If a column can change, it should be added to each event as a normal column
    await addOrUpdateCommonProperties(
        insights[analyticsInstanceKey],
        analyticsOptions,
        skipCommonProperties,
        useUnifiedSchema,
        diagnosticOverrides ?? undefined,
        accountKey
    );

    let offlineListener: IOfflineListener | undefined;
    let unsubscribe: (() => void) | undefined;
    if (getAnalyticsFlightsAndAppSettings(accountKey)?.enableOfflineCaching) {
        // OfflineChannel is not initialized properly if added during initialization
        // so we add it here via updateCfg / addPlugin
        const offlineChannel = new OfflineChannel();
        const idbProvider: eStorageProviders.IndexedDb = 3;
        const offlineConfig: IOfflineChannelConfiguration = {
            providers: [idbProvider],
            storageKeyPrefix: getOfflineTelemetryDatabasePrefix(tenantToken),
        };
        insights[analyticsInstanceKey].updateCfg({
            extensionConfig: { [offlineChannel.identifier]: offlineConfig },
        });
        insights[analyticsInstanceKey].addPlugin(offlineChannel);

        // The client is responsible for telling the OfflineChannel when it is online vs. offline
        offlineListener = offlineChannel.getOfflineListener();
        const offlineValue: eOfflineValue.Offline = 2;
        const onlineValue: eOfflineValue.Online = 1;
        offlineListener?.setOnlineState(self.navigator.onLine ? onlineValue : offlineValue);
        // Fix (PR 228162): the original callback wrapped the setOnlineState
        // call in an inner arrow function expression that was never invoked,
        // making network state transitions a no-op.
        unsubscribe = subscribeToNetworkChange((onLine: boolean) => {
            offlineListener?.setOnlineState(onLine ? onlineValue : offlineValue);
        });
    }

    cachedProperties[analyticsInstanceKey] = {
        offlineListener,
        unsubscribe,
        accountKey,
        skipCommonProperties,
        useUnifiedSchema,
    };
}

function gzipFunc(payload: IPayloadData, cb: (data: IPayloadData) => void) {
    try {
        const dataToSend = pako.gzip(payload.data);
        const headers = payload.headers || {};
        headers['Content-Encoding'] = 'gzip';
        payload.headers = headers;
        payload.data = dataToSend;
        cb(payload);
    } catch {
        // send original payload on error
        return cb(payload);
    }
}

/**
 * Internal helper. Exported for tests only — do not call from production code.
 */
export async function __TEST_ONLY_addOrUpdateCommonProperties(
    appInsights: ApplicationInsights,
    analyticsOptions?: CoreAnalyticsOptions,
    skipCommonProperties?: boolean,
    useUnifiedSchema?: boolean,
    diagnosticOverrides?: DiagnosticOverrides,
    accountKey: AccountKey | 'globalAccount' = 'globalAccount'
): Promise<void> {
    return addOrUpdateCommonProperties(
        appInsights,
        analyticsOptions,
        skipCommonProperties,
        useUnifiedSchema,
        diagnosticOverrides,
        accountKey
    );
}

async function addOrUpdateCommonProperties(
    appInsights: ApplicationInsights,
    analyticsOptions?: CoreAnalyticsOptions,
    skipCommonProperties?: boolean,
    useUnifiedSchema?: boolean,
    diagnosticOverrides?: DiagnosticOverrides,
    accountKey: AccountKey | 'globalAccount' = 'globalAccount'
) {
    if (diagnosticOverrides) {
        diagnosticOverrides.CommonPropertiesStatus = 'PENDING';
    }

    try {
        const propertyManager = appInsights.getPropertyManager();
        if (!skipCommonProperties && !useUnifiedSchema && analyticsOptions) {
            await addCommonProperties(propertyManager, analyticsOptions, accountKey);
        }

        // Stamp the ECS configuration ETag as a per-tenant common property.
        // This lands as a Part C custom property on every event in the StringMap,
        // letting the IDEAs join (NonOtel_ECS_IntInterface.view) recover the
        // full flight-assignment config-id list from the ETag at query time.
        // Stamping is gated by the same kill switch that gates ECS config-id
        // stamping, and no-ops when the ETag is absent from the boot response.
        //
        // The ETag is read from the analytics-cached-settings cache rather than
        // from owa-application-settings directly. The cache is keyed by account
        // and is also synced to worker threads (AnalyticsWorker, DataWorker),
        // so this single read site works on every thread and naturally returns
        // the per-account value when an account key is supplied.
        //
        // Late-ECS race: if the ECS configuration resolves after a 1DS tenant
        // is already initialized, that tenant keeps the ETag value the cache
        // had at init time and will not re-stamp. Tenant init is typically
        // lazy on first event, by which point ECS has usually resolved, so
        // this gap is accepted rather than mitigated with a separate
        // re-stamping subscription. See PR for tracking notes.
        const stampingDisabled =
            getAnalyticsFlightsAndAppSettings(accountKey)?.disableEcsETagStamping === true;
        if (!stampingDisabled) {
            const ecsETag = getCachedSettings(accountKey)?.ecsETag;
            if (ecsETag) {
                propertyManager.setProperty('EcsETag', ecsETag);
                const mapping = getCachedSettings(accountKey)?.ecsConfigState;
                if (mapping?.etag === ecsETag && mapping.configIds) {
                    captureSdkEcsConfigState(appInsights, mapping);
                }
            }
        }

        const context = propertyManager.getPropertiesContext();
        if (context) {
            context.app.ver = getClientVersion();
            context.session.setId(getSessionId());
            context.web.domain = undefined;
            // We do not set the local deviceId because it can cause the network request to fail
            // if the formatting is not correct. We log it as a separate property instead.
            context.device.localId = undefined;
            if (analyticsOptions?.overrideOsVersion) {
                context.os.ver = analyticsOptions.overrideOsVersion;
            }
        }

        if (diagnosticOverrides) {
            diagnosticOverrides.CommonPropertiesStatus = 'SUCCESS';
        }
    } catch (e) {
        if (diagnosticOverrides) {
            diagnosticOverrides.CommonPropertiesStatus = 'ERROR';
            // Errors resulting from enabling useUnknownInCatchVariables
            // TS2339: Property 'message' does not exist on type '{}'.
            // @ts-expect-error
            diagnosticOverrides.CommonPropertiesError = e?.message;
        }
    }
}
